import dayjs from "dayjs";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useCampaignFormDataQuery, useSingleCampaign } from "../../App/Queries/CampaignQueries";
import { useCampaignGroup } from "../../App/Queries/GroupQueries";
import { ICampaignFormState } from "../../App/Typings/Campaigns";
import DialogState from "../../ManageCampaigns/State/DialogState";
import EmbedScriptState from "../../ManageCampaigns/State/EmbedScriptState";
import useDeleteCampaignDialogState from "./DeleteCampaignDialogState";

export type CampaignProfileState = ReturnType<typeof useCampaignProfileState>;

export default function useCampaignProfileState() {
  const { id = "" } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: campaign, isFetching, isLoading, error } = useSingleCampaign(id);
  const { data: campaignGroup } = useCampaignGroup(campaign?.id ?? "");
  const { dialogProps, handleDisplayDialog, handleClearDialog } = DialogState();
  const { handleSubmitDeleteCampaign, handleOpenDeleteDialog } = useDeleteCampaignDialogState(
    handleDisplayDialog,
    handleClearDialog,
    () => navigate("/leadcapture/forms"),
  );
  const { handleOpenEmbedScriptDialog } = EmbedScriptState(handleDisplayDialog, handleClearDialog);

  const { data: formData } = useCampaignFormDataQuery<ICampaignFormState>(
    campaign?.id ?? "",
    campaign?.forms[0]?.id ?? "",
  );

  const getQueryDate = (key: string) => {
    const queryDate = searchParams.get(key);
    if (queryDate) {
      return dayjs(queryDate).toDate();
    }

    return undefined;
  };

  return {
    getters: {
      campaign,
      campaignGroup,
      dialogProps,
      isFetching,
      isLoading,
      error,
      formData,
      startDate: getQueryDate("startdate"),
      endDate: getQueryDate("enddate"),
      lastUpdatedDate: campaign?.lastUpdatedDate?.toISOString() ?? new Date().toISOString(),
    },
    handlers: {
      handleClearDialog,
      handleSubmitDeleteCampaign,
      handleOpenDeleteDialog,
      handleOpenEmbedScriptDialog,
    },
  } as const;
}
